import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Device } from "@twilio/voice-sdk";
import { twilioAPI, callsAPI, adminAPI, speedDialAPI, departmentsAPI } from "../../services/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  PhoneForwarded,
  Mic,
  MicOff,
  Pause,
  Play,
  X,
  Minimize2,
  Maximize2,
  ArrowRightLeft,
  Building2,
  Heart,
  GraduationCap,
  Clock,
  Zap,
  Bell,
  Search,
  Users,
  User,
  MessageSquare,
  Grid3X3,
  ParkingCircle,
  Inbox,
  UserPlus,
  FileText,
} from "lucide-react";
import { formatPhoneNumber, formatDuration, cn } from "../../lib/utils";
import {
  RECOMMENDED_DEVICE_OPTIONS,
  RECOMMENDED_MIC_CONSTRAINTS,
  attachCallQualityListeners,
} from "../../lib/twilioCallQuality";
import { toast } from "sonner";
import PostCallDocumentationModal from "../communications/PostCallDocumentationModal";
import SaveContactForm from "../communications/SaveContactForm";
import CallControls from "../phone/CallControls";

// Dev-only diagnostic logger; stripped automatically in production builds
// via the NODE_ENV guard. Uses console.log — previously (pre-iter-428)
// this was self-referential (`dlog(...args)`) which would have caused an
// infinite recursion had anything actually invoked it in a dev build.
const dlog = (...args) => { if (process.env.NODE_ENV !== "production") console.log(...args); };

// Context for sharing call state across pages
const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

const COMPANY_CONFIG = {
  "+14143269510": { name: "Bylyngo Interpreting", short: "Bylyngo", color: "#FC6A03", icon: Building2, logo: "/logos/bylyngo-logo.png", greeting: "Thank you for calling Bylyngo Interpreting & Translation — how can I help you?" },
  "+14143484600": { name: "Avida Health", short: "Avida", color: "#2563EB", icon: Heart, logo: "/logos/avida-logo.png", greeting: "Thank you for calling Avida Personal Care — how can I help you?" },
  "+14143269501": { name: "Bylyngo Institute", short: "Institute", color: "#BB2000", icon: GraduationCap, logo: "/logos/bylyngo-logo.png", greeting: "Thank you for calling Bylyngo Institute — how can I help you?" },
  "+12627778410": { name: "Main Line", short: "Main", color: "#00346f", icon: Phone, logo: "/logos/calllyngo-logo.png", greeting: "Thank you for calling — how may I direct your call?" },
};
const getCompanyFromNumber = (num) => COMPANY_CONFIG[num] || { name: "Unknown", short: "Unknown", color: "#00346f", icon: Building2, logo: null, greeting: "Thank you for calling — how can I help you?" };

const TRANSFER_DESTINATIONS = [
  { name: "Bylyngo Interpreting", number: "+14143269510", color: "navy", icon: Building2 },
  { name: "Avida Personal Care", number: "+14143484600", color: "olive", icon: Heart },
  { name: "Bylyngo Institute", number: "+14143269501", color: "red", icon: GraduationCap },
];

export function CallProvider({ user, children }) {
  const location = useLocation();
  const [deviceReady, setDeviceReady] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [minimized, setMinimized] = useState(false);
  // When a richer on-page softphone is mounted (e.g. Ring Center's floating
  // softphone), it suppresses this global overlay so only ONE active-call UI shows.
  const [suppressOverlay, setSuppressOverlay] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferNumber, setTransferNumber] = useState("");
  const [transferSearch, setTransferSearch] = useState("");
  const [speedDials, setSpeedDials] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [contacts, setContacts] = useState([]);
  // Warm-transfer state machine. Loaded once when the overlay mounts.
  // 'idle'        — no transfer happening
  // 'consulting'  — caller on hold, agent A is now talking to colleague B
  const [warmTransferTargets, setWarmTransferTargets] = useState([]);
  const [warmTransfer, setWarmTransfer] = useState({ state: "idle", targetName: null });
  const [showDtmfPad, setShowDtmfPad] = useState(false);
  const [dtmfDigits, setDtmfDigits] = useState("");
  // Live call notes — agent jots key points + action items during the
  // call; on hang-up they pre-fill the post-call documentation log.
  const [showNotes, setShowNotes] = useState(false);
  const [callNotes, setCallNotes] = useState({ key_points: "", action_items: "" });
  // Call Park — slots state shared across the overlay (Park modal) AND
  // the floating "Parked Calls" pickup tray (visible to any agent when
  // a call is waiting in a park slot).
  const [parkSlots, setParkSlots] = useState([]);
  const [showParkModal, setShowParkModal] = useState(false);
  const [parking, setParking] = useState(false);
  const [pickingUpSlot, setPickingUpSlot] = useState(null);
  // Post-call documentation — captured on disconnect so the modal can
  // pre-fill phone + direction + duration. Dawn: "more documentation
  // of the calls, texts and communication". Optional per the policy
  // we picked (3c) — agent can skip but the prompt is right there.
  const [postCallContext, setPostCallContext] = useState(null);
  // Whether the "Save Contact" mini-form modal is open for the currently
  // active call. Set to the caller phone string (not a boolean) so the
  // modal always has the phone to pre-fill even if the call ends first.
  const [saveContactPhone, setSaveContactPhone] = useState(null);

  // ── "S" keyboard shortcut ──────────────────────────────────────────
  // Press S at any time during an active call to open the Save Contact
  // form — pill click still works, this is just the no-mouse path.
  // Guards:
  //   • Only fires while a call is active
  //   • Skipped when typing in an input/textarea/contenteditable
  //   • Ignored when the modal itself is already open (avoids re-open)
  //   • Requires plain S — modifier keys (Cmd/Ctrl/Alt/Meta) skip so
  //     Ctrl+S "save" and browser find-in-page keep working.
  useEffect(() => {
    if (!activeCall) return;
    const handler = (e) => {
      if (e.key !== "s" && e.key !== "S") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (saveContactPhone) return;
      const t = e.target;
      if (t && (
        t.tagName === "INPUT" ||
        t.tagName === "TEXTAREA" ||
        t.tagName === "SELECT" ||
        t.isContentEditable
      )) return;
      e.preventDefault();
      const phone = activeCall.direction === "inbound" ? activeCall.from : activeCall.to;
      setSaveContactPhone(phone);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activeCall, saveContactPhone]);
  // Traffic-light grade for the live call's network ('good'|'fair'|'poor').
  // Updated from Twilio's `sample` events (~1 Hz). Resets to 'good' between calls.
  const [connectionQuality, setConnectionQuality] = useState("good");
  // Detach fn returned by attachCallQualityListeners(); cleared between calls.
  const qualityCleanupRef = useRef(null);
  // Mirror refs so the disconnect handler (which runs AFTER React has
  // already cleared state) can still see the values it needs for the
  // post-call documentation modal.
  const activeCallRef = useRef(null);
  const callDurationRef = useRef(0);
  // Mirror of the live call-notes so the SDK 'disconnect' handler can read
  // the latest jotted text after React has already wiped the call state.
  const callNotesRef = useRef({ key_points: "", action_items: "" });

  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const ringtoneRef = useRef(null);
  const timerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const ringtoneAudioRef = useRef(null);

  const notificationRef = useRef(null);

  // Keep refs in sync with state so the SDK 'disconnect' handler — which
  // fires AFTER React has wiped activeCall — can still read the values
  // needed to populate the post-call documentation modal.
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  useEffect(() => { callDurationRef.current = callDuration; }, [callDuration]);
  useEffect(() => { callNotesRef.current = callNotes; }, [callNotes]);

  // Pre-initialize audio context on user gesture (Connect Phone click)
  const ensureAudioContext = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  // Soft two-tone "park" chime — a gentle ascending pair (G5→C6), lower
  // gain and shorter than the ringtone so it reads as a passive alert, not
  // an inbound call. Best-effort: silently no-ops if audio is blocked.
  const playParkChime = useCallback(() => {
    try {
      const ctx = ensureAudioContext();
      if (!ctx) return;
      const now = ctx.currentTime;
      const tones = [{ f: 784, t: 0 }, { f: 1047, t: 0.16 }];
      tones.forEach(({ f, t }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        const start = now + t;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.4);
      });
    } catch { /* audio may be blocked until first gesture — ignore */ }
  }, [ensureAudioContext]);


  // Generate a ringtone WAV blob (US ring: 440Hz + 480Hz, 1 second).
  // Transfers use a distinct higher pitch (523/659Hz) so a hand-off is
  // audibly different from a fresh inbound call.
  const generateRingtoneBlob = useCallback((f1 = 440, f2 = 480) => {
    const sampleRate = 44100;
    const duration = 1.0;
    const numSamples = Math.floor(sampleRate * duration);
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);
    const writeStr = (offset, str) => { for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); };
    writeStr(0, "RIFF");
    view.setUint32(4, 36 + numSamples * 2, true);
    writeStr(8, "WAVE");
    writeStr(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, "data");
    view.setUint32(40, numSamples * 2, true);
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const sample = 0.3 * (Math.sin(2 * Math.PI * f1 * t) + Math.sin(2 * Math.PI * f2 * t));
      view.setInt16(44 + i * 2, Math.max(-32768, Math.min(32767, Math.floor(sample * 32767))), true);
    }
    return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
  }, []);

  // Reliable ringtone: HTML5 Audio primary, Web Audio fallback
  const createRingtone = useCallback((opts = {}) => {
    const [f1, f2] = opts.transfer ? [523.25, 659.25] : [440, 480];
    let playing = false;
    let intervalId = null;
    let audioEl = null;
    let webAudioOscs = [];
    let webAudioTimeouts = [];

    const playWithHtml5 = () => {
      if (!playing) return;
      try {
        if (!audioEl) {
          audioEl = new Audio(generateRingtoneBlob(f1, f2));
          audioEl.volume = 1.0;
        }
        audioEl.currentTime = 0;
        const p = audioEl.play();
        if (p) p.catch(() => tryWebAudioFallback());
      } catch {
        tryWebAudioFallback();
      }
    };

    const tryWebAudioFallback = () => {
      const ctx = audioCtxRef.current;
      if (!ctx || !playing) return;
      ctx.resume().then(() => {
        if (!playing) return;
        try {
          const osc1 = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          const gain = ctx.createGain();
          osc1.frequency.value = f1;
          osc2.frequency.value = f2;
          gain.gain.value = 0.3;
          osc1.connect(gain); osc2.connect(gain); gain.connect(ctx.destination);
          osc1.start(); osc2.start();
          webAudioOscs.push(osc1, osc2);
          const tid = setTimeout(() => {
            try { osc1.stop(); osc2.stop(); } catch {}
          }, 1000);
          webAudioTimeouts.push(tid);
        } catch {}
      }).catch(() => {});
    };

    return {
      play: () => {
        playing = true;
        dlog("[TeamAvida] Ringtone starting");
        playWithHtml5();
        // Ring pattern: 1s on, 2s off
        intervalId = setInterval(() => {
          if (playing) playWithHtml5();
        }, 3000);
      },
      stop: () => {
        dlog("[TeamAvida] Ringtone stopping");
        playing = false;
        if (intervalId) { clearInterval(intervalId); intervalId = null; }
        if (audioEl) { audioEl.pause(); audioEl.currentTime = 0; audioEl = null; }
        webAudioTimeouts.forEach(id => clearTimeout(id));
        webAudioOscs.forEach(osc => { try { osc.stop(); } catch {} });
        webAudioOscs = [];
        webAudioTimeouts = [];
      }
    };
  }, [generateRingtoneBlob]);

  // Timer
  const startTimer = () => { setCallDuration(0); timerRef.current = setInterval(() => setCallDuration(p => p + 1), 1000); };
  const stopTimer = () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; setCallDuration(0); };
  const stopRingtone = () => {
    if (ringtoneRef.current) { ringtoneRef.current.stop(); ringtoneRef.current = null; }
    if (notificationRef.current) { notificationRef.current.close(); notificationRef.current = null; }
  };

  const initializeDevice = useCallback(async (opts = {}) => {
    const { silent = false, isUserGesture = false } = opts;
    if (deviceRef.current || initializing) return;

    // Safari strictly requires getUserMedia to be called from within a user
    // gesture handler. If this is an auto-invocation (mount / tab-visible),
    // detect Safari and bail out quietly so the user can explicitly click the
    // "Connect" button — otherwise we'd spam "Permission denied" toasts.
    const ua = (navigator.userAgent || "").toLowerCase();
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent || "")
      || (ua.includes("safari") && !ua.includes("chrome") && !ua.includes("android"));
    if (isSafari && !isUserGesture) {
      dlog("[TeamAvida] Safari detected — deferring phone init to user-gesture click");
      return;
    }

    setInitializing(true);
    try {
      // Pre-initialize audio context on user gesture (critical for ringtone)
      ensureAudioContext();

      await navigator.mediaDevices.getUserMedia(RECOMMENDED_MIC_CONSTRAINTS);
      const tokenRes = await twilioAPI.getToken(user.id, true, false);
      const device = new Device(tokenRes.data.token, RECOMMENDED_DEVICE_OPTIONS);

      device.on("registered", () => setDeviceReady(true));
      device.on("unregistered", () => setDeviceReady(false));
      device.on("error", (err) => {
        console.error("[TeamAvida] Device error:", err?.message || err);
        const msg = err?.message || "Unknown error";
        if (msg.includes("AccessTokenExpired") || msg.includes("expired")) {
          toast.error("Phone session expired — reconnecting...");
          deviceRef.current?.destroy();
          deviceRef.current = null;
          setDeviceReady(false);
          setTimeout(() => initializeDevice({ isUserGesture: true }), 1000);
        } else if (msg.includes("AccessTokenInvalid")) {
          toast.error("Phone token invalid — reconnecting...");
          deviceRef.current?.destroy();
          deviceRef.current = null;
          setDeviceReady(false);
          setTimeout(() => initializeDevice({ isUserGesture: true }), 2000);
        } else if (msg.includes("31009") || msg.includes("transport") || msg.includes("signaling")) {
          dlog("[TeamAvida] Connection lost — auto-reconnecting...");
          deviceRef.current?.destroy();
          deviceRef.current = null;
          setDeviceReady(false);
          setTimeout(() => initializeDevice({ isUserGesture: true }), 3000);
        } else {
          toast.error(`Phone error: ${msg}`);
        }
      });
      device.on("incoming", (call) => {
        const from = call.parameters.From;
        const to = call.parameters.To;
        const callSid = call.parameters.CallSid;
        const companyNumber = call.customParameters?.get?.("CompanyNumber");
        let company, callerNumber;
        if (companyNumber) {
          company = getCompanyFromNumber(companyNumber);
          callerNumber = from;
        } else if (COMPANY_CONFIG[from]) {
          company = getCompanyFromNumber(from);
          callerNumber = "Looking up caller...";
          // Use /current-caller endpoint (queries by company phone, NO race condition)
          const lookupCaller = async (retriesLeft = 4) => {
            try {
              const res = await twilioAPI.getCurrentCaller(from);
              if (res.data?.from_number) {
                setIncomingCall(prev => prev ? { ...prev, from: res.data.from_number } : null);
              } else if (retriesLeft > 0) {
                setTimeout(() => lookupCaller(retriesLeft - 1), 1200);
              } else {
                setIncomingCall(prev => prev ? { ...prev, from: "Unknown Caller" } : null);
              }
            } catch {
              if (retriesLeft > 0) {
                setTimeout(() => lookupCaller(retriesLeft - 1), 1200);
              } else {
                setIncomingCall(prev => prev ? { ...prev, from: "Unknown Caller" } : null);
              }
            }
          };
          lookupCaller();
        } else {
          company = getCompanyFromNumber(to);
          callerNumber = from;
        }

        // ── Transfer awareness ──────────────────────────────────────────
        // Blind transfers to a browser client carry context inline via
        // Twilio <Parameter> custom params. Warm transfers don't, so we ask
        // the backend (below) whether this ringing SID is a hand-off.
        const tFrom = call.customParameters?.get?.("TransferFrom");
        const tOrig = call.customParameters?.get?.("OriginalCaller");
        const tType = call.customParameters?.get?.("TransferType");
        let transfer = null;
        if (tFrom) {
          transfer = { by: tFrom, originalCaller: tOrig || callerNumber, type: tType || "blind" };
          if (tOrig) callerNumber = tOrig;
        }

        setIncomingCall({ call, from: callerNumber, to: companyNumber || from, company, transfer });
        ringtoneRef.current = createRingtone({ transfer: !!transfer }); ringtoneRef.current.play();

        // Warm transfer (no inline params) — confirm with the backend and,
        // if it's a hand-off, re-badge the ring screen + swap to the
        // transfer ringtone.
        if (!transfer && callSid) {
          twilioAPI.getIncomingContext(callSid).then((res) => {
            if (res?.data?.is_transfer) {
              setIncomingCall((prev) => {
                if (!prev) return null;
                stopRingtone();
                ringtoneRef.current = createRingtone({ transfer: true });
                ringtoneRef.current.play();
                return {
                  ...prev,
                  transfer: {
                    by: res.data.transferred_by,
                    originalCaller: res.data.original_caller || prev.from,
                    type: res.data.transfer_type || "warm",
                  },
                  from: res.data.original_caller || prev.from,
                };
              });
            }
          }).catch(() => {});
        }

        // SDK events for hangup detection (best-effort, may not fire via REST API cancel)
        call.on("cancel", () => { dlog("[TeamAvida] SDK cancel"); setIncomingCall(null); stopRingtone(); });
        call.on("disconnect", () => { dlog("[TeamAvida] SDK disconnect"); setIncomingCall(null); stopRingtone(); });
        call.on("reject", () => { dlog("[TeamAvida] SDK reject"); setIncomingCall(null); stopRingtone(); });

        // Desktop notification (works even when tab is minimized)
        if ("Notification" in window && Notification.permission === "granted") {
          const n = new Notification(`Incoming Call - ${company.name}`, {
            body: `From: ${formatPhoneNumber(from)}\nClick to answer`,
            icon: "/favicon.ico",
            tag: "incoming-call",
            requireInteraction: true,
            silent: false,
          });
          n.onclick = () => { window.focus(); n.close(); };
          notificationRef.current = n;
        }
      });

      await device.register();
      deviceRef.current = device;
      // Remember that this user successfully connected at least once on this
      // browser — future mounts can auto-init without the Safari user-gesture
      // guard (the mic permission is already granted for the origin).
      try { localStorage.setItem("phone_auto_connect", "1"); } catch {}
      // Request notification permission
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch (err) {
      console.error("Device init:", err);
      const name = err?.name || "";
      const msg = err?.message || "";
      // Use err.name (reliable) instead of message string-matching (varies per
      // browser / locale). Keep the silent flag so auto-invocations on dev
      // environments without a mic don't spam toasts.
      if (name === "NotAllowedError" || /permission/i.test(msg)) {
        if (!silent) {
          toast.error("Microphone access denied — click the mic icon in your browser's address bar to allow Avida to use your microphone, then click Connect again.", { duration: 8000 });
        }
        // If Safari blocked us because we weren't in a gesture, clear the
        // auto-connect flag so next mount doesn't keep retrying silently.
        try { localStorage.removeItem("phone_auto_connect"); } catch {}
      } else if (name === "NotFoundError" || /device not found/i.test(msg)) {
        if (!silent) {
          toast.error("No microphone found — please connect a mic, then click Connect again.");
        }
      } else if (name === "NotReadableError") {
        if (!silent) {
          toast.error("Microphone is in use by another app — close other apps using the mic, then click Connect again.");
        }
      } else if (!silent) {
        // Surface Twilio's actual error code + explanation when present.
        // Voice SDK errors carry .code (e.g. 31201 = Access Token Error,
        // 20003 = auth failed, 20404 = resource not found) and .explanation.
        // Prior version collapsed everything to "unknown error", which
        // hides the exact credential that's broken.
        const code = err?.code ? ` [code ${err.code}]` : "";
        const explanation = err?.explanation ? ` — ${err.explanation}` : "";
        toast.error(
          `Failed to connect phone${code}: ${msg || name || "unknown error"}${explanation}`,
          { duration: 10000 }
        );
      }
    } finally {
      setInitializing(false);
    }
  }, [user?.id, initializing, ensureAudioContext]);

  // Helper — attach quality listeners and store the cleanup fn.
  // Called on every call (inbound + outbound) right after we have a Call
  // instance. The returned cleanup is invoked when the call ends so we
  // don't leak listeners across calls.
  const wireQuality = useCallback((call) => {
    if (!call) return;
    // Reset to optimistic 'good' until the first sample arrives (~1 sec).
    setConnectionQuality("good");
    qualityCleanupRef.current?.();
    qualityCleanupRef.current = attachCallQualityListeners(call, {
      onQualityChange: (grade) => setConnectionQuality(grade),
      onWarning: (message) => {
        // Single transient toast; the live dot keeps showing the state.
        toast.warning(message, { duration: 4000, id: "call-quality-warning" });
      },
      onWarningCleared: () => {
        toast.dismiss("call-quality-warning");
      },
    });
  }, []);

  const answerCall = () => {
    if (!incomingCall) return;
    incomingCall.call.accept();
    callRef.current = incomingCall.call;
    setActiveCall({ ...incomingCall, direction: "inbound", status: "connected" });
    setIncomingCall(null);
    setMinimized(false);
    setCallNotes({ key_points: "", action_items: "" });
    setShowNotes(false);
    stopRingtone();
    startTimer();
    wireQuality(incomingCall.call);
    incomingCall.call.on("disconnect", () => {
      qualityCleanupRef.current?.(); qualityCleanupRef.current = null;
      setConnectionQuality("good");
      _captureForPostCallDoc();
      setActiveCall(null); callRef.current = null; setIsMuted(false); setIsOnHold(false); setIsRecording(false); setShowDtmfPad(false); setDtmfDigits(""); stopTimer();
    });
  };

  const rejectCall = () => { if (!incomingCall) return; incomingCall.call.reject(); setIncomingCall(null); stopRingtone(); };

  const makeCall = async (toNumber, fromNumber) => {
    if (!deviceRef.current || !deviceReady) { toast.error("Phone not connected"); return; }
    if (activeCall) { toast.error("Already on a call"); return; }
    try {
      const isQueueCall = toNumber.startsWith("queue:");
      const company = getCompanyFromNumber(fromNumber);
      const call = await deviceRef.current.connect({ params: { To: toNumber, From: fromNumber, CallerId: fromNumber } });
      callRef.current = call;
      setActiveCall({ call, from: fromNumber, to: isQueueCall ? "Queue Call" : toNumber, company, direction: isQueueCall ? "inbound" : "outbound", status: "connecting" });
      setMinimized(false);
      setCallNotes({ key_points: "", action_items: "" });
      setShowNotes(false);
      startTimer();
      wireQuality(call);
      call.on("accept", () => setActiveCall(p => p ? { ...p, status: "connected" } : null));
      call.on("disconnect", () => {
        qualityCleanupRef.current?.(); qualityCleanupRef.current = null;
        setConnectionQuality("good");
        _captureForPostCallDoc();
        setActiveCall(null); callRef.current = null; setIsMuted(false); setIsOnHold(false); setIsRecording(false); setShowDtmfPad(false); setDtmfDigits(""); stopTimer();
      });
      call.on("cancel", () => {
        qualityCleanupRef.current?.(); qualityCleanupRef.current = null;
        setConnectionQuality("good");
        // Twilio SDK fires "cancel" WITHOUT an error event when the call never
        // successfully connected on the carrier side (rejected caller ID, TwiML
        // App misroute, unroutable number, etc.). Without a hint here the agent
        // just sees the call vanish — very hard to debug.
        //
        // We ONLY show the toast when the call was still in "connecting" state
        // (never accepted). If the user hung up during an active call, cancel
        // fires too but that's expected and we stay silent.
        setActiveCall(prev => {
          if (prev?.status === "connecting" && prev.direction === "outbound") {
            toast.error(
              "Call didn't connect. Admins: open /phone → Diagnose to see Twilio's error code.",
              { duration: 6000 }
            );
          }
          return null;
        });
        callRef.current = null; setShowDtmfPad(false); setDtmfDigits(""); stopTimer();
      });
      call.on("error", (err) => {
        console.error("[TeamAvida] Outbound call error:", err?.code, err?.message, err);
        const code = err?.code;
        const msg = err?.message || "Unknown error";
        // Twilio-specific error codes — surface the real cause instead of a generic toast
        // Reference: https://www.twilio.com/docs/api/errors
        if (code === 31002 || /connection declined/i.test(msg)) toast.error(`Carrier declined the call (${code}). CallerID may not be verified.`);
        else if (code === 31003 || /timeout/i.test(msg)) toast.error("Call timed out — number didn't answer or carrier unreachable.");
        else if (code === 31005 || /transport/i.test(msg)) toast.error("Lost connection to phone server. Try reconnecting the phone.");
        else if (code === 31008 || /cancelled/i.test(msg)) toast.error("Call was cancelled.");
        else if (code === 31009 || /signaling/i.test(msg)) toast.error("Signaling error — phone needs to reconnect.");
        else if (/not registered|unregistered/i.test(msg)) toast.error("Phone not registered with carrier — click Connect.");
        else if (/permission|microphone|getusermedia/i.test(msg)) toast.error("Microphone access denied — check browser permissions.");
        else toast.error(`Call failed: ${msg}${code ? ` (code ${code})` : ""}`);
      });
    } catch (err) {
      console.error("[TeamAvida] makeCall failed:", err?.code, err?.message, err);
      const msg = err?.message || "Unknown error";
      if (/permission|microphone|getusermedia/i.test(msg)) toast.error("Microphone access denied — check browser permissions.");
      else if (/not registered|unregistered|deviceReady/i.test(msg)) toast.error("Phone not connected — click the Connect button.");
      else toast.error(`Failed to connect call: ${msg}`);
    }
  };

  const endCall = () => callRef.current?.disconnect();
  const toggleMute = () => { if (!callRef.current) return; callRef.current.mute(!isMuted); setIsMuted(!isMuted); };
  const toggleHold = async () => {
    if (!activeCall) return;
    try {
      const sid = callRef.current?.parameters?.CallSid;
      if (sid) { await callsAPI.holdCall(sid, !isOnHold); setIsOnHold(!isOnHold); toast.success(isOnHold ? "Resumed" : "On hold"); }
    } catch { toast.error("Failed to toggle hold"); }
  };
  const transferCall = async (target) => {
    if (!activeCall || !target) return;
    try {
      const sid = callRef.current?.parameters?.CallSid;
      if (sid) { await callsAPI.transferCall(sid, target); toast.success(`Transferring to ${formatPhoneNumber(target)}`); setShowTransferDialog(false); setTransferNumber(""); }
    } catch { toast.error("Transfer failed"); }
  };

  // ── Merge — dial a second party into the live call as a conference ──
  const mergeCall = async (target) => {
    if (!activeCall || !target) { toast.error("No active call to merge into"); return; }
    const sid = callRef.current?.parameters?.CallSid;
    if (!sid) { toast.error("Can't resolve the active call"); return; }
    setIsMerging(true);
    try {
      await callsAPI.mergeCall(sid, target);
      toast.success(`Merging ${formatPhoneNumber(target)} into the call…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setIsMerging(false);
    }
  };

  // ── Record — explicit operator-driven start/stop on the live call ──
  const toggleRecording = async () => {
    if (!activeCall) return;
    const sid = callRef.current?.parameters?.CallSid;
    if (!sid) { toast.error("Can't resolve the active call"); return; }
    const next = !isRecording;
    try {
      await callsAPI.toggleRecording(sid, next);
      setIsRecording(next);
      toast.success(next ? "Recording started" : "Recording stopped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recording toggle failed");
    }
  };

  // ── Warm transfer (internal colleague hand-off) ───────────────────
  // Backend lifecycle in routers/twilio.py — these three handlers call
  // POST /twilio/transfer-call → /complete-transfer → /cancel-transfer.
  const warmTransferToColleague = useCallback(async (target) => {
    if (!callRef.current || !target?.id) return;
    const sid = callRef.current.parameters?.CallSid;
    if (!sid) return;
    setWarmTransfer({ state: "calling", targetName: `${target.first_name || ""} ${target.last_name || ""}`.trim() || target.email });
    try {
      await callsAPI.warmTransferCall(sid, target.id);
      // Caller is now on hold; agent A is being connected to colleague B
      // via the conference. Flip the local hold flag so the UI shows it.
      setIsOnHold(true);
      setWarmTransfer({ state: "consulting", targetName: `${target.first_name || ""} ${target.last_name || ""}`.trim() || target.email });
      toast.success(`Consulting with ${target.first_name || target.email}…`);
    } catch (e) {
      setWarmTransfer({ state: "idle", targetName: null });
      toast.error(e?.response?.data?.detail || "Transfer failed");
    }
  }, []);

  // Warm-transfer to a raw desk extension / phone number (consult first).
  const warmTransferToNumber = useCallback(async (number, displayName) => {
    const raw = (number || "").trim();
    if (!callRef.current || !raw) return;
    const sid = callRef.current.parameters?.CallSid;
    if (!sid) return;
    const shown = (displayName || "").trim() || raw;
    setWarmTransfer({ state: "calling", targetName: shown });
    try {
      await callsAPI.warmTransferCall(sid, null, raw);
      setIsOnHold(true);
      setWarmTransfer({ state: "consulting", targetName: shown });
      toast.success(`Consulting with ${shown}…`);
    } catch (e) {
      setWarmTransfer({ state: "idle", targetName: null });
      toast.error(e?.response?.data?.detail || "Transfer failed");
    }
  }, []);
  // (falls back to the first assigned teammate if none are marked available).
  const transferToDepartment = useCallback((dept) => {
    const members = dept?.members || [];
    if (members.length === 0) {
      toast.error(`No one is assigned to ${dept?.name || "this department"}`);
      return;
    }
    const target = members.find(m => m.available) || members[0];
    warmTransferToColleague({
      id: target.id,
      first_name: target.first_name,
      last_name: target.last_name,
      email: target.email,
    });
  }, [warmTransferToColleague]);

  const completeWarmTransfer = useCallback(async () => {
    if (!callRef.current) return;
    const sid = callRef.current.parameters?.CallSid;
    if (!sid) return;
    try {
      await callsAPI.completeTransfer(sid);
      toast.success(`Transferred to ${warmTransfer.targetName}`);
      // Our own leg has been hung up by the backend — the SDK's
      // 'disconnect' handler will clean up activeCall + timers.
      setWarmTransfer({ state: "idle", targetName: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete transfer");
    }
  }, [warmTransfer.targetName]);

  const cancelWarmTransfer = useCallback(async () => {
    if (!callRef.current) return;
    const sid = callRef.current.parameters?.CallSid;
    if (!sid) return;
    try {
      await callsAPI.cancelTransfer(sid);
      setIsOnHold(false);
      setWarmTransfer({ state: "idle", targetName: null });
      toast.success("Transfer cancelled — you're back with the caller");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel transfer");
    }
  }, []);

  // Capture the just-ended call's info so the post-call modal can
  // pre-fill phone/direction/duration without us having to grab it
  // after `activeCall` has already been wiped. Skipped for calls
  // shorter than 3 seconds (likely an immediate hangup / misdial).
  // 5s was too aggressive — real conversations sometimes end in 4s
  // when someone confirms "wrong number, sorry" and hangs up, and
  // those still deserve a log entry.
  const _captureForPostCallDoc = useCallback(() => {
    const ac = activeCallRef.current;
    const dur = callDurationRef.current || 0;
    if (!ac || dur < 3) return;
    const phone = ac.direction === "outbound" ? ac.to : ac.from;
    // Fold the live call notes (key points + action items) into a single
    // summary string so the post-call log opens pre-filled.
    const n = callNotesRef.current || {};
    const noteParts = [];
    if (n.key_points?.trim()) noteParts.push(n.key_points.trim());
    if (n.action_items?.trim()) noteParts.push(`Action items:\n${n.action_items.trim()}`);
    const notesText = noteParts.join("\n\n");
    setPostCallContext({
      phone,
      direction: ac.direction || "inbound",
      duration_sec: dur,
      call_sid: ac.call?.parameters?.CallSid || null,
      company_name: ac.company?.name || null,
      notes: notesText || null,
    });
    // Auto-log the call to the matched client's relationship log (fire-and-forget).
    // Backend matches the number to a client by phone; no-op if none matches.
    try {
      const token = localStorage.getItem("token");
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/healthcare/relationship-log/auto-log-call`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          phone,
          direction: ac.direction || "inbound",
          duration_sec: dur,
          call_sid: ac.call?.parameters?.CallSid || null,
        }),
      }).catch(() => {});
    } catch { /* noop */ }
  }, []);
  // Park the active call into a numbered slot. If `slot` is omitted the
  // backend picks the lowest free slot. On success agent A's leg is
  // hung up automatically; the SDK's 'disconnect' handler clears state.

  // Auto-callback watchdog mirror — caller is automatically returned
  // to the original parker if nobody picks up within 3 minutes
  // (server-enforced; UI shows the countdown only).
  const PARK_TIMEOUT_SECONDS = 180;

  // Tick once per second to drive live countdown labels in the modal /
  // tray. We use a ref-less state increment so React re-renders.
  const [parkTick, setParkTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => { if (document.hidden) return; setParkTick(v => (v + 1) % 1_000_000); }, 1000);
    return () => clearInterval(t);
  }, []);

  // Compute remaining seconds until the 3-min auto-callback fires.
  // Returns null for empty slots / missing parked_at; clamps to 0 below.
  const parkSecondsRemaining = useCallback((slot) => {
    if (!slot?.parked_at) return null;
    const startedMs = Date.parse(slot.parked_at);
    if (Number.isNaN(startedMs)) return null;
    const elapsedSec = (Date.now() - startedMs) / 1000;
    return Math.max(0, PARK_TIMEOUT_SECONDS - Math.floor(elapsedSec));
  }, [parkTick]); // intentional: re-eval each tick

  // Format seconds → "M:SS"
  const formatParkCountdown = useCallback((sec) => {
    if (sec == null) return "";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }, []);

  const parkActiveCall = useCallback(async (slot = null) => {
    if (!callRef.current) return;
    const sid = callRef.current.parameters?.CallSid;
    if (!sid) return;
    setParking(true);
    try {
      const res = await callsAPI.parkCall(sid, slot);
      toast.success(`Parked on slot ${res.data.slot} — teammates can pick up`);
      setShowParkModal(false);
      // Refresh the shared tray so other tabs see it immediately.
      refreshParkSlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not park the call");
    } finally {
      setParking(false);
    }
  }, []);

  const prevParkedRef = useRef(null); // null = not yet primed (skip chime on first load)
  const refreshParkSlots = useCallback(async () => {
    try {
      const r = await callsAPI.getParkedCalls();
      const slots = r.data?.slots || [];
      setParkSlots(slots);

      // Detect NEWLY parked callers → soft chime + toast so any free agent
      // knows someone is waiting, even with the softphone closed. Skip on the
      // first poll (priming) and skip calls this agent parked themselves.
      const occNow = new Map(slots.filter(s => s.occupied).map(s => [s.slot, s]));
      const prev = prevParkedRef.current;
      if (prev !== null) {
        for (const [slot, s] of occNow) {
          const wasThere = prev.has(slot);
          if (!wasThere && s.parked_by_user_id !== user?.id) {
            const name = (s.label && s.label.trim()) ? `Park ${slot} · ${s.label}` : `Park ${slot}`;
            playParkChime();
            toast(`Caller waiting in ${name}`, {
              description: s.parked_by_name ? `Parked by ${s.parked_by_name}` : "Tap the dialer to pick up",
              duration: 8000,
            });
          }
        }
      }
      prevParkedRef.current = new Set(occNow.keys());
    } catch {
      /* silent — polling is best-effort */
    }
  }, [playParkChime, user?.id]);

  // Auto-return alert: when a caller this agent parked times out (3 min) and
  // is auto-rung back to them, the slot leaves /parked-calls, so we poll a
  // dedicated endpoint and fire a chime + toast so the incoming ring isn't a
  // silent surprise. Dedupe by event id (primes silently on first poll).
  const seenReturnsRef = useRef(null);
  const refreshParkAutoReturns = useCallback(async () => {
    try {
      const r = await callsAPI.getParkAutoReturns();
      const events = r.data?.events || [];
      const seen = seenReturnsRef.current;
      if (seen !== null) {
        for (const ev of events) {
          if (ev.id && !seen.has(ev.id)) {
            const name = (ev.label && ev.label.trim()) ? `Park ${ev.slot} · ${ev.label}` : `Park ${ev.slot}`;
            const who = ev.company_name || (ev.caller_number ? formatPhoneNumber(ev.caller_number) : "your caller");
            playParkChime();
            toast(`${name} is returning to you`, {
              description: `${who} timed out after 3 min — answer the incoming call.`,
              duration: 10000,
            });
          }
        }
      }
      seenReturnsRef.current = new Set(events.map((e) => e.id).filter(Boolean));
    } catch {
      /* best-effort */
    }
  }, [playParkChime]);


  const pickupParkedCall = useCallback(async (slot) => {
    setPickingUpSlot(slot);
    try {
      await callsAPI.pickupParkedCall(slot);
      toast.success(`Picking up Park ${slot}… your phone will ring`);
      // Slot will free within a poll cycle once Twilio fires the
      // bridge; refresh now for a snappier UX.
      setTimeout(refreshParkSlots, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pick up the parked call");
    } finally {
      setPickingUpSlot(null);
    }
  }, [refreshParkSlots]);

  const sendDTMF = (digit) => {
    if (!callRef.current) return;
    callRef.current.sendDigits(digit);
    setDtmfDigits(prev => prev + digit);
  };

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
    speedDialAPI.getAll().then(r => setSpeedDials(r.data || [])).catch(() => {});
    import("../../services/api").then(({ contactsAPI }) => {
      contactsAPI.getAll().then(r => setContacts(r.data || [])).catch(() => {});
    });
    // Warm-transfer roster — eligible colleagues only. Cheap call;
    // fetched once on mount.
    callsAPI.getTransferTargets()
      .then(r => setWarmTransferTargets(r.data?.targets || []))
      .catch(() => {});
    departmentsAPI.list()
      .then(r => setDepartments((r.data?.departments || []).filter(d => d.active !== false && (d.member_user_ids || []).length > 0)))
      .catch(() => {});

    // Park slots — fetch immediately and then poll every 5s so the
    // pickup tray reflects what other teammates have parked. The
    // 3-minute auto-callback watchdog runs server-side; this poll
    // just keeps the per-slot countdown UI roughly in sync.
    refreshParkSlots();
    refreshParkAutoReturns();
    const parkPollInterval = setInterval(() => { if (document.hidden) return; refreshParkSlots(); refreshParkAutoReturns(); }, 5000);

    // Auto-connect phone on login (non-Safari browsers where the flag has
    // been set from a prior successful connect). Safari ALWAYS requires an
    // explicit click on "Connect" because Apple only allows getUserMedia from
    // a synchronous user gesture — any async / auto-invocation will fail with
    // NotAllowedError even if the user previously granted mic permission.
    if (user?.id && !deviceRef.current && !initializing) {
      const hasConnectedBefore = (() => { try { return localStorage.getItem("phone_auto_connect") === "1"; } catch { return false; } })();
      const ua = (navigator.userAgent || "").toLowerCase();
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent || "")
        || (ua.includes("safari") && !ua.includes("chrome") && !ua.includes("android"));
      if (!isSafari && hasConnectedBefore) {
        // Silent auto-init — swallow errors so dev/no-mic environments don't
        // spam "Failed to connect phone" on every page load.
        initializeDevice({ silent: true, isUserGesture: false });
      }
    }

    // Proactive token refresh — refresh every 12 hours (token TTL is 24h)
    const tokenRefreshInterval = setInterval(() => {
      if (deviceRef.current && deviceReady) {
        dlog("[TeamAvida] Proactive token refresh...");
        twilioAPI.getToken(user.id, true, false).then(tokenRes => {
          deviceRef.current.updateToken(tokenRes.data.token);
          dlog("[TeamAvida] Token refreshed successfully");
        }).catch(err => {
          console.error("[TeamAvida] Token refresh failed:", err);
        });
      }
    }, 12 * 60 * 60 * 1000);

    // Reconnect on tab visibility change (browser may kill WebSocket when inactive)
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        if (deviceRef.current) {
          // Check if device is still registered, re-register if not
          if (deviceRef.current.state !== "registered") {
            dlog("[TeamAvida] Tab visible, device unregistered — reconnecting...");
            deviceRef.current.destroy();
            deviceRef.current = null;
            setDeviceReady(false);
            // Mic was previously granted this session → bypass Safari gesture guard
            setTimeout(() => initializeDevice({ isUserGesture: true, silent: true }), 500);
          } else {
            // Device seems registered, refresh token as a precaution
            twilioAPI.getToken(user.id, true, false).then(tokenRes => {
              deviceRef.current?.updateToken(tokenRes.data.token);
            }).catch(() => {});
          }
        } else if (!initializing) {
          // Device was destroyed while tab was hidden, reinitialize only if
          // the user had connected before (don't auto-prompt Safari users).
          const hadConnected = (() => { try { return localStorage.getItem("phone_auto_connect") === "1"; } catch { return false; } })();
          if (hadConnected) {
            dlog("[TeamAvida] Tab visible, no device — initializing...");
            initializeDevice({ isUserGesture: true, silent: true });
          }
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Periodic registration health check — every 30 seconds
    const healthCheckInterval = setInterval(() => {
      // Only reconnect if device exists but lost registration, and no active call
      if (deviceRef.current && deviceRef.current.state === "unregistered" && !activeCall) {
        dlog("[TeamAvida] Health check: device unregistered, attempting re-register...");
        try { deviceRef.current.register(); } catch {}
      }
    }, 60000);

    // Unlock AudioContext silently on first user interaction
    const unlockAudioSilent = () => {
      if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === "suspended") {
        audioCtxRef.current.resume();
      }
    };
    document.addEventListener("click", unlockAudioSilent, { once: true });

    return () => {
      stopRingtone(); stopTimer();
      clearInterval(tokenRefreshInterval);
      clearInterval(healthCheckInterval);
      clearInterval(parkPollInterval);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (deviceRef.current) deviceRef.current.destroy();
      document.removeEventListener("click", unlockAudioSilent);
    };
  }, [user?.id]);

  const notifPermission = "Notification" in window ? Notification.permission : "unavailable";
  const requestNotifPermission = async () => {
    if ("Notification" in window && Notification.permission !== "granted") {
      await Notification.requestPermission();
    }
  };

  // The old floating "Connect / Enable Phone" banner was removed — its
  // connect action now lives permanently in the Ring Center top bar
  // (rc-connect-phone-btn), driven by the deviceReady/initializing/
  // initializeDevice values exposed on this context.

  const ctx = { deviceReady, initializing, activeCall, incomingCall, callDuration, isMuted, isOnHold, isRecording, isMerging, connectionQuality, initializeDevice, makeCall, answerCall, rejectCall, endCall, toggleMute, toggleHold, transferCall, mergeCall, toggleRecording, warmTransferToColleague, warmTransferToNumber, transferToDepartment, departments, completeWarmTransfer, cancelWarmTransfer, warmTransfer, warmTransferTargets, sendDTMF, speedDials, notifPermission, requestNotifPermission, parkSlots, parkActiveCall, refreshParkSlots, pickupParkedCall, suppressOverlay, setSuppressOverlay, callNotes, setCallNotes };

  return (
    <CallContext.Provider value={ctx}>
      {children}

      {/* ======= INCOMING CALL BANNER ======= */}
      {incomingCall && (
        <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)", minHeight: "100vh" }} data-testid="incoming-call-banner">
          <div className="w-[460px] rounded-3xl shadow-2xl overflow-hidden bg-white border border-slate-200 animate-in fade-in zoom-in-95 duration-300" data-testid={incomingCall.transfer ? "incoming-transfer-card" : "incoming-call-card"}>
            {/* Transfer banner — only for hand-offs from a colleague */}
            {incomingCall.transfer && (
              <div className="px-6 py-3 flex items-center gap-3 bg-amber-500" data-testid="transfer-banner">
                <div className="w-9 h-9 rounded-full bg-white/25 flex items-center justify-center animate-pulse shrink-0">
                  <ArrowRightLeft className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[11px] font-bold uppercase tracking-wider">
                    {incomingCall.transfer.type === "warm" ? "Warm Transfer" : "Transferred Call"}
                  </p>
                  <p className="text-white text-base font-black truncate" data-testid="transfer-from-name">
                    Transferred by {incomingCall.transfer.by}
                  </p>
                </div>
              </div>
            )}
            {/* Company brand banner — SOLID brand color for instant
                Avida-vs-ByLyngo recognition when one operator fronts both. */}
            <div className="px-6 py-4 flex items-center gap-4" style={{ background: incomingCall.company.color }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 overflow-hidden bg-white/95 shadow-sm">
                {incomingCall.company.logo ? (
                  <img src={incomingCall.company.logo} alt={incomingCall.company.name} className="w-9 h-9 object-contain" />
                ) : (
                  (() => { const I = incomingCall.company.icon; return <I className="w-7 h-7" style={{ color: incomingCall.company.color }} />; })()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/80 text-[11px] font-bold uppercase tracking-widest">{incomingCall.transfer ? "Transfer via" : "Incoming Call"}</p>
                <p className="text-xl font-black text-white tracking-tight truncate" data-testid="incoming-company-name">{incomingCall.company.name}</p>
              </div>
              <div className="w-11 h-11 rounded-full flex items-center justify-center animate-pulse shrink-0 bg-white/20">
                {incomingCall.transfer ? <PhoneForwarded className="w-5 h-5 text-white" /> : <PhoneIncoming className="w-5 h-5 text-white" />}
              </div>
            </div>
            {/* Caller info */}
            <div className="px-6 pt-5 pb-3">
              <p className="text-slate-400 text-[11px] mb-1 uppercase tracking-wider font-semibold">{incomingCall.transfer ? "Original caller" : "Caller"}</p>
              <p className="text-3xl font-bold text-slate-900 font-mono tracking-tight" data-testid="incoming-caller-number">{formatPhoneNumber(incomingCall.from)}</p>
            </div>
            {/* Greeting script — the exact opening line for THIS brand, so the
                operator answers on-script every time. */}
            {incomingCall.company.greeting && !incomingCall.transfer && (
              <div className="mx-6 mb-4 rounded-xl px-4 py-3 border" data-testid="incoming-greeting-script"
                   style={{ background: `${incomingCall.company.color}0F`, borderColor: `${incomingCall.company.color}33` }}>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: incomingCall.company.color }}>Answer with</p>
                <p className="text-sm font-medium text-slate-700 leading-snug">“{incomingCall.company.greeting}”</p>
              </div>
            )}
            {/* Action buttons — big round, RingCentral style */}
            <div className="bg-slate-50 px-6 py-6 flex items-start justify-center gap-10 border-t border-slate-100">
              <button onClick={rejectCall} data-testid="reject-call-btn" className="flex flex-col items-center gap-2 group focus-visible:outline-none">
                <span className="w-16 h-16 rounded-full bg-red-500 group-hover:bg-red-600 flex items-center justify-center shadow-lg shadow-red-500/30 transition-transform group-active:scale-95">
                  <PhoneOff className="w-7 h-7 text-white" />
                </span>
                <span className="text-xs font-semibold text-slate-600">Decline</span>
              </button>
              <button onClick={answerCall} data-testid="answer-call-btn" className="flex flex-col items-center gap-2 group focus-visible:outline-none">
                <span className="w-20 h-20 rounded-full bg-green-500 group-hover:bg-green-600 flex items-center justify-center shadow-lg shadow-green-500/40 animate-pulse transition-transform group-active:scale-95">
                  <Phone className="w-9 h-9 text-white" />
                </span>
                <span className="text-sm font-bold text-slate-800">Answer</span>
              </button>
              <button onClick={() => { rejectCall(); toast.info("Sent to voicemail"); }} data-testid="voicemail-call-btn" className="flex flex-col items-center gap-2 group focus-visible:outline-none">
                <span className="w-16 h-16 rounded-full bg-slate-200 group-hover:bg-slate-300 flex items-center justify-center transition-transform group-active:scale-95">
                  <MessageSquare className="w-7 h-7 text-slate-600" />
                </span>
                <span className="text-xs font-semibold text-slate-600">Voicemail</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======= ACTIVE CALL OVERLAY ======= */}
      {/* Hidden while an on-page softphone (Ring Center) is showing its own call UI. */}
      {activeCall && !suppressOverlay && (
        <div className={cn(
          "fixed z-[90] transition-all duration-300",
          minimized ? "bottom-4 right-4 w-[300px]" : "top-4 right-4 w-[400px]"
        )} data-testid="active-call-overlay">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
            {/* Header — company accent + live caller info */}
            <div className="h-1.5 w-full" style={{ background: activeCall.company?.color || "#032866" }} />
            <div className="px-5 pt-4 pb-4 flex items-start gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 overflow-hidden border border-slate-100 shadow-sm"
                style={{ background: activeCall.company?.color ? `${activeCall.company.color}14` : "#f1f5f9" }}
              >
                {activeCall.company?.logo ? (
                  <img src={activeCall.company.logo} alt={activeCall.company.name} className="w-8 h-8 object-contain" />
                ) : (
                  (() => { const I = activeCall.company?.icon || Phone; return <I className="w-5 h-5" style={{ color: activeCall.company?.color || "#032866" }} />; })()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider truncate" style={{ color: activeCall.company?.color || "#032866" }}>{activeCall.company?.name || "Active Call"}</p>
                <p className="text-2xl text-slate-900 font-bold truncate font-mono tracking-tight leading-tight">
                  {formatPhoneNumber(activeCall.direction === "inbound" ? activeCall.from : activeCall.to)}
                </p>
                {/* Save-Contact pill — always available during a live
                    call so agents can capture unknown callers in real
                    time instead of waiting for the post-call modal.
                    Hidden when the overlay is minimized to keep the
                    compact chip clean. */}
                {!minimized && (
                  <button
                    type="button"
                    onClick={() => setSaveContactPhone(activeCall.direction === "inbound" ? activeCall.from : activeCall.to)}
                    className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-slate-100 hover:bg-slate-200 text-slate-600 transition"
                    data-testid="live-call-save-contact"
                    title="Save this caller as a new contact (press S)"
                  >
                    <UserPlus className="w-3 h-3" /> Save Contact
                    <kbd
                      className="ml-1 px-1 py-[1px] rounded-sm bg-slate-200 text-slate-500 text-[9px] font-black leading-none tracking-normal"
                      aria-hidden="true"
                    >
                      S
                    </kbd>
                  </button>
                )}
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0 rounded-full" onClick={() => setMinimized(!minimized)} data-testid="overlay-minimize-btn">
                {minimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </Button>
            </div>

            {/* Status row — quality dot + timer + on-hold badge */}
            <div className="px-5 py-3 bg-slate-50 border-y border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "w-2.5 h-2.5 rounded-full transition-colors shrink-0",
                    connectionQuality === "good" && "bg-emerald-500",
                    connectionQuality === "fair" && "bg-amber-500 animate-pulse",
                    connectionQuality === "poor" && "bg-red-500 animate-pulse",
                  )}
                  title={
                    connectionQuality === "good"
                      ? "Connection: good"
                      : connectionQuality === "fair"
                      ? "Connection: fair — minor jitter or latency"
                      : "Connection: poor — packet loss / high jitter / low MOS"
                  }
                  data-testid="call-quality-indicator"
                  data-quality={connectionQuality}
                />
                <span className="text-xs text-slate-500 font-medium capitalize">{connectionQuality}</span>
                {isOnHold && (
                  <Badge className="ml-2 bg-amber-100 text-amber-700 border-0 text-[10px] animate-pulse" data-testid="on-hold-badge">ON HOLD</Badge>
                )}
              </div>
              <span className="text-slate-900 text-xl font-mono font-bold tracking-tight tabular-nums" data-testid="call-duration">{formatDuration(callDuration)}</span>
            </div>

            {/* Controls (hidden when minimized) */}
            {!minimized && (
              <div className="px-5 pt-4 pb-5">
                {/* DTMF Digits display */}
                {dtmfDigits && (
                  <div className="text-center mb-3">
                    <span className="text-slate-800 text-lg font-mono font-bold tracking-widest">{dtmfDigits}</span>
                  </div>
                )}

                {/* DTMF Keypad */}
                {showDtmfPad && (
                  <div className="mb-4">
                    <div className="grid grid-cols-3 gap-2">
                      {["1","2","3","4","5","6","7","8","9","*","0","#"].map(digit => (
                        <button
                          key={digit}
                          onClick={() => sendDTMF(digit)}
                          className="h-12 rounded-xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 text-lg font-semibold transition-colors duration-100"
                          data-testid={`dtmf-${digit}`}
                        >
                          {digit}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warm-transfer consultation banner */}
                {warmTransfer.state !== "idle" && (
                  <div
                    className="mb-4 p-3 rounded-xl flex items-center gap-3 bg-emerald-50 border border-emerald-200"
                    data-testid="warm-transfer-banner"
                  >
                    <PhoneForwarded className="w-5 h-5 text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {warmTransfer.state === "calling" ? "Ringing " : "Consulting with "}
                        {warmTransfer.targetName}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        Caller is on hold with music. {warmTransfer.state === "consulting" ? "Brief them, then complete or cancel." : "Waiting for them to pick up…"}
                      </p>
                    </div>
                    {warmTransfer.state === "consulting" && (
                      <>
                        <Button
                          size="sm"
                          className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-3"
                          onClick={completeWarmTransfer}
                          data-testid="complete-transfer-btn"
                        >
                          Complete
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 text-slate-600 hover:bg-slate-100 text-xs px-3"
                          onClick={cancelWarmTransfer}
                          data-testid="cancel-transfer-btn"
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* Call notes — jot key points + action items live; they
                    pre-fill the post-call documentation log on hang-up. */}
                {showNotes && (
                  <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="call-notes-panel">
                    <div className="flex items-center gap-1.5 mb-2">
                      <FileText className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Call Notes</span>
                      <span className="ml-auto text-[10px] text-slate-400">Saved to the call log</span>
                    </div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Key points</label>
                    <textarea
                      value={callNotes.key_points}
                      onChange={(e) => setCallNotes(n => ({ ...n, key_points: e.target.value }))}
                      rows={2}
                      placeholder="What was discussed, concerns, changes in condition…"
                      data-testid="call-notes-key-points"
                      className="w-full text-sm text-slate-800 rounded-lg border border-slate-200 bg-white px-2.5 py-2 outline-none focus:border-blue-400 resize-none mb-2"
                    />
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Action items</label>
                    <textarea
                      value={callNotes.action_items}
                      onChange={(e) => setCallNotes(n => ({ ...n, action_items: e.target.value }))}
                      rows={2}
                      placeholder="Follow-ups, next steps, who owns them…"
                      data-testid="call-notes-action-items"
                      className="w-full text-sm text-slate-800 rounded-lg border border-slate-200 bg-white px-2.5 py-2 outline-none focus:border-blue-400 resize-none"
                    />
                  </div>
                )}

                {/* Primary controls — big round buttons in RingCentral style.
                    Mute / Hold / Transfer come from the shared <CallControls>
                    (rendered via display:contents so they share this grid);
                    Keypad + Park are overlay-only. Hang-up is the big red
                    button rendered separately below for prominence. */}
                <div className="grid grid-cols-3 gap-y-4 gap-x-2 justify-items-center">
                  <CallControls variant="grid" hideHangup />

                  {/* Keypad */}
                  <button
                    onClick={() => setShowDtmfPad(!showDtmfPad)}
                    title="Keypad"
                    data-testid="dtmf-btn"
                    className="flex flex-col items-center gap-1.5 group focus-visible:outline-none"
                  >
                    <span className={cn(
                      "w-14 h-14 rounded-full flex items-center justify-center transition-colors",
                      showDtmfPad ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-700 group-hover:bg-slate-200"
                    )}>
                      <Grid3X3 className="w-6 h-6" />
                    </span>
                    <span className="text-xs font-medium text-slate-600">Keypad</span>
                  </button>

                  {/* Park */}
                  <button
                    onClick={() => { refreshParkSlots(); setShowParkModal(true); }}
                    title="Park"
                    data-testid="park-btn"
                    className="flex flex-col items-center gap-1.5 group focus-visible:outline-none"
                  >
                    <span className="w-14 h-14 rounded-full flex items-center justify-center bg-slate-100 text-slate-700 group-hover:bg-slate-200 transition-colors">
                      <ParkingCircle className="w-6 h-6" />
                    </span>
                    <span className="text-xs font-medium text-slate-600">Park</span>
                  </button>

                  {/* Notes */}
                  <button
                    onClick={() => setShowNotes(!showNotes)}
                    title="Call notes"
                    data-testid="call-notes-btn"
                    className="flex flex-col items-center gap-1.5 group focus-visible:outline-none"
                  >
                    <span className={cn(
                      "w-14 h-14 rounded-full flex items-center justify-center transition-colors",
                      showNotes ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-700 group-hover:bg-slate-200"
                    )}>
                      <FileText className="w-6 h-6" />
                    </span>
                    <span className="text-xs font-medium text-slate-600">Notes</span>
                  </button>
                </div>

                {/* Prominent hang-up — big red circle, centered */}
                <div className="mt-6 flex flex-col items-center gap-1.5">
                  <button
                    onClick={endCall}
                    title="End call"
                    data-testid="active-call-hangup-btn"
                    className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-500/30 transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                  >
                    <PhoneOff className="w-7 h-7" />
                  </button>
                  <span className="text-xs font-semibold text-slate-500">End Call</span>
                </div>

                {/* Legacy rich Transfer picker retained (dropdown originally
                    triggered by the in-grid transfer button). This picker is
                    now surfaced via its own kebab-style anchor because the
                    primary "Transfer" affordance in <CallControls> only
                    exposes Business Lines + Speed Dials. Departments,
                    Colleagues and generic Contacts still need the richer
                    picker below, opened from a small "More…" button. */}
                <div className="hidden">
                  <DropdownMenu onOpenChange={(open) => { if (!open) setTransferSearch(""); }}>
                    <DropdownMenuTrigger asChild>
                      <button
                        title="Transfer"
                        data-testid="transfer-btn"
                        className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-slate-800 text-slate-200 hover:bg-slate-700 transition-colors"
                      >
                        <ArrowRightLeft className="w-5 h-5" />
                        <span className="text-[10px] font-medium">Transfer</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 max-h-[400px] overflow-hidden" data-testid="transfer-menu">
                      {/* Search bar */}
                      <div className="px-2 py-2 border-b border-slate-100">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
                          <input
                            className="w-full h-7 pl-7 pr-2 text-sm bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
                            placeholder="Search contacts..."
                            value={transferSearch}
                            onChange={e => setTransferSearch(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            data-testid="transfer-search-input"
                          />
                        </div>
                      </div>

                      <div className="max-h-[320px] overflow-y-auto">
                        {departments.length > 0 && (!transferSearch || departments.some(d => d.name.toLowerCase().includes(transferSearch.toLowerCase()))) && (
                          <>
                            <DropdownMenuLabel className="text-[10px] text-slate-400 uppercase tracking-wider px-3 pt-2 pb-1">Departments (warm transfer)</DropdownMenuLabel>
                            {departments
                              .filter(d => !transferSearch || d.name.toLowerCase().includes(transferSearch.toLowerCase()))
                              .map(d => (
                                <DropdownMenuItem
                                  key={d.id}
                                  onClick={() => transferToDepartment(d)}
                                  className="flex items-center gap-2.5 py-2 mx-1 rounded-md cursor-pointer"
                                  data-testid={`transfer-department-${d.key}`}
                                >
                                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-indigo-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{d.name}</p>
                                    <p className="text-[11px] text-slate-400 truncate">
                                      {d.available_count > 0 ? `${d.available_count} available · consult first` : "no one available now"}
                                    </p>
                                  </div>
                                  <PhoneForwarded className="w-3.5 h-3.5 text-indigo-400" />
                                </DropdownMenuItem>
                              ))}
                            <DropdownMenuSeparator />
                          </>
                        )}

                        {warmTransferTargets.length > 0 && (!transferSearch || warmTransferTargets.some(t => `${t.first_name} ${t.last_name}`.toLowerCase().includes(transferSearch.toLowerCase()))) && (
                          <>
                            <DropdownMenuLabel className="text-[10px] text-slate-400 uppercase tracking-wider px-3 pt-2 pb-1">Colleagues (warm transfer)</DropdownMenuLabel>
                            {warmTransferTargets
                              .filter(t => !transferSearch || `${t.first_name || ""} ${t.last_name || ""}`.toLowerCase().includes(transferSearch.toLowerCase()))
                              .slice(0, 8)
                              .map(t => (
                                <DropdownMenuItem
                                  key={t.id}
                                  onClick={() => warmTransferToColleague(t)}
                                  className="flex items-center gap-2.5 py-2 mx-1 rounded-md cursor-pointer"
                                  data-testid={`warm-transfer-target-${t.id}`}
                                >
                                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                                    <User className="w-4 h-4 text-emerald-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{t.first_name} {t.last_name}</p>
                                    <p className="text-[11px] text-slate-400 truncate">{t.status === "available" ? "Available" : t.status} · consult first</p>
                                  </div>
                                  <PhoneForwarded className="w-3.5 h-3.5 text-emerald-400" />
                                </DropdownMenuItem>
                              ))}
                            <DropdownMenuSeparator />
                          </>
                        )}

                        {(!transferSearch || TRANSFER_DESTINATIONS.some(d => d.name.toLowerCase().includes(transferSearch.toLowerCase()))) && (
                          <>
                            <DropdownMenuLabel className="text-[10px] text-slate-400 uppercase tracking-wider px-3 pt-2 pb-1">Business Lines</DropdownMenuLabel>
                            {TRANSFER_DESTINATIONS
                              .filter(d => !transferSearch || d.name.toLowerCase().includes(transferSearch.toLowerCase()))
                              .map(d => {
                                const I = d.icon;
                                return (
                                  <DropdownMenuItem key={d.number} onClick={() => transferCall(d.number)} className="flex items-center gap-2.5 py-2 mx-1 rounded-md cursor-pointer">
                                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br",
                                      d.color === "navy" ? "from-navy-500 to-navy-600" :
                                      d.color === "olive" ? "from-olive-500 to-olive-600" :
                                      "from-blue-500 to-blue-600"
                                    )}>
                                      <I className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">{d.name}</p>
                                      <p className="text-[11px] text-slate-400 font-mono">{formatPhoneNumber(d.number)}</p>
                                    </div>
                                    <PhoneForwarded className="w-3.5 h-3.5 text-slate-300" />
                                  </DropdownMenuItem>
                                );
                              })}
                          </>
                        )}

                        {speedDials.length > 0 && (!transferSearch || speedDials.some(s => s.name.toLowerCase().includes(transferSearch.toLowerCase()))) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[10px] text-slate-400 uppercase tracking-wider px-3 pt-1 pb-1">Speed Dials</DropdownMenuLabel>
                            {speedDials
                              .filter(sd => !transferSearch || sd.name.toLowerCase().includes(transferSearch.toLowerCase()))
                              .map(sd => (
                                <DropdownMenuItem key={sd.id} onClick={() => transferCall(sd.phone_number)} className="flex items-center gap-2.5 py-2 mx-1 rounded-md cursor-pointer">
                                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                                    <Zap className="w-4 h-4 text-amber-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{sd.name}</p>
                                    <p className="text-[11px] text-slate-400 font-mono">{formatPhoneNumber(sd.phone_number)}</p>
                                  </div>
                                  <PhoneForwarded className="w-3.5 h-3.5 text-slate-300" />
                                </DropdownMenuItem>
                              ))}
                          </>
                        )}

                        {contacts.length > 0 && (!transferSearch || contacts.some(c => `${c.first_name} ${c.last_name}`.toLowerCase().includes(transferSearch.toLowerCase()))) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[10px] text-slate-400 uppercase tracking-wider px-3 pt-1 pb-1">Contacts</DropdownMenuLabel>
                            {contacts
                              .filter(c => !transferSearch || `${c.first_name} ${c.last_name} ${c.phone_number}`.toLowerCase().includes(transferSearch.toLowerCase()))
                              .slice(0, 8)
                              .map(c => (
                                <DropdownMenuItem key={c.id} onClick={() => transferCall(c.phone_number)} className="flex items-center gap-2.5 py-2 mx-1 rounded-md cursor-pointer">
                                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                                    <User className="w-4 h-4 text-slate-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{c.first_name} {c.last_name}</p>
                                    <p className="text-[11px] text-slate-400 font-mono">{formatPhoneNumber(c.phone_number)}</p>
                                  </div>
                                  <PhoneForwarded className="w-3.5 h-3.5 text-slate-300" />
                                </DropdownMenuItem>
                              ))}
                          </>
                        )}

                        {transferSearch &&
                          !TRANSFER_DESTINATIONS.some(d => d.name.toLowerCase().includes(transferSearch.toLowerCase())) &&
                          !speedDials.some(s => s.name.toLowerCase().includes(transferSearch.toLowerCase())) &&
                          !contacts.some(c => `${c.first_name} ${c.last_name}`.toLowerCase().includes(transferSearch.toLowerCase())) && (
                          <div className="px-3 py-4 text-center text-sm text-slate-400">
                            No matches found
                          </div>
                        )}

                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowTransferDialog(true)} className="flex items-center gap-2.5 py-2 mx-1 rounded-md cursor-pointer">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <PhoneForwarded className="w-4 h-4 text-blue-500" />
                          </div>
                          <span className="text-sm font-medium text-blue-600">Enter custom number...</span>
                        </DropdownMenuItem>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* End-call is now rendered by <CallControls> above. The
                    old dedicated red button lived here for fat-finger safety
                    but the shared component keeps it visually distinct (red
                    background) in the primary row — same protection. */}
              </div>
            )}

            {/* Minimized: just show end button */}
            {minimized && (
              <div className="px-4 py-2.5 flex items-center justify-end gap-2">
                <Button size="sm" variant="ghost" className={cn("text-xs h-8", isMuted ? "text-red-500" : "text-slate-500")} onClick={toggleMute}>{isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
                <Button size="sm" variant="ghost" className={cn("text-xs h-8", isOnHold ? "text-amber-500" : "text-slate-500")} onClick={toggleHold}>{isOnHold ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}</Button>
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white h-8 text-xs px-3 rounded-full" onClick={endCall}><PhoneOff className="w-3.5 h-3.5 mr-1" /> End</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======= PARK MODAL — slot grid with current occupants ======= */}
      <Dialog open={showParkModal} onOpenChange={setShowParkModal}>
        <DialogContent className="sm:max-w-md" data-testid="park-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ParkingCircle className="w-5 h-5 text-blue-500" /> Park Call
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-500">Pick a slot. Any teammate can grab the call from the same screen. Auto-returns to you after 3 minutes.</p>
            <div className="grid grid-cols-1 gap-2">
              {parkSlots.length === 0 && (
                <div className="text-center py-6 text-slate-400 text-sm">Loading park slots…</div>
              )}
              {parkSlots.map(s => {
                const occupied = !!s.occupied;
                const remaining = occupied ? parkSecondsRemaining(s) : null;
                const urgent = remaining != null && remaining <= 60;
                return (
                  <button
                    key={s.slot}
                    disabled={occupied || parking}
                    onClick={() => parkActiveCall(s.slot)}
                    data-testid={`park-slot-${s.slot}`}
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-colors text-left",
                      occupied
                        ? (urgent ? "border-red-300 bg-red-50 cursor-not-allowed" : "border-amber-300 bg-amber-50 cursor-not-allowed")
                        : "border-slate-200 hover:border-blue-400 hover:bg-blue-50"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg shrink-0",
                        occupied
                          ? (urgent ? "bg-red-500 text-white" : "bg-amber-500 text-white")
                          : "bg-blue-500 text-white"
                      )}>
                        {s.slot}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">Park {s.slot}</p>
                        {occupied ? (
                          <p className="text-[11px] text-slate-600 font-mono truncate">
                            {formatPhoneNumber(s.caller_number) || "Unknown caller"}
                            {s.parked_by_name ? ` · by ${s.parked_by_name}` : ""}
                          </p>
                        ) : (
                          <p className="text-[11px] text-slate-400">Empty — tap to park here</p>
                        )}
                      </div>
                    </div>
                    {occupied ? (
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={cn("text-white border-0", urgent ? "bg-red-500 animate-pulse" : "bg-amber-500")}>Occupied</Badge>
                        <span
                          className={cn("text-[10px] font-mono font-bold tabular-nums", urgent ? "text-red-600" : "text-slate-500")}
                          title="Auto-returns to parker at 0:00"
                          data-testid={`park-countdown-${s.slot}`}
                        >
                          {formatParkCountdown(remaining)}
                        </span>
                      </div>
                    ) : (
                      <Badge className="bg-emerald-500 text-white border-0">Free</Badge>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="pt-2 flex gap-2">
              <Button
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                disabled={parking || parkSlots.every(s => s.occupied)}
                onClick={() => parkActiveCall(null)}
                data-testid="park-auto-btn"
              >
                <ParkingCircle className="w-4 h-4 mr-2" /> Park in first open slot
              </Button>
              <Button variant="outline" onClick={() => setShowParkModal(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ======= PARKED CALLS PICKUP TRAY ========
          Floats in the bottom-left for ANY agent (even those without an
          active call) so they can pick up calls a teammate has parked.
          Hidden when there are no occupied slots AND the agent is already
          on a call (no reason to show them; clutter). */}
      {(() => {
        const occupiedSlots = parkSlots.filter(s => s.occupied);
        if (occupiedSlots.length === 0) return null;
        if (activeCall) return null; // suppress to avoid double-overlay clutter
        return (
          <div
            className="fixed bottom-4 left-4 z-[85] w-[300px] rounded-2xl shadow-2xl overflow-hidden border-2 border-amber-300 bg-white"
            data-testid="parked-calls-tray"
          >
            <div className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 flex items-center gap-2">
              <Inbox className="w-4 h-4 text-white" />
              <p className="text-white text-xs font-bold uppercase tracking-wider">Parked Calls</p>
              <Badge className="ml-auto bg-white/20 text-white border-0 text-[10px]">{occupiedSlots.length}</Badge>
            </div>
            <div className="max-h-[280px] overflow-y-auto divide-y divide-slate-100">
              {occupiedSlots.map(s => {
                const remaining = parkSecondsRemaining(s);
                const urgent = remaining != null && remaining <= 60;
                return (
                  <button
                    key={s.slot}
                    onClick={() => pickupParkedCall(s.slot)}
                    disabled={pickingUpSlot === s.slot}
                    data-testid={`pickup-park-${s.slot}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-50 text-left transition-colors disabled:opacity-50"
                  >
                    <div className={cn(
                      "w-9 h-9 rounded-lg text-white flex items-center justify-center font-bold text-base shrink-0",
                      urgent ? "bg-red-500 animate-pulse" : "bg-amber-500"
                    )}>
                      {s.slot}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-900 truncate font-mono">{formatPhoneNumber(s.caller_number) || "Unknown caller"}</p>
                        <span
                          className={cn("text-[10px] font-mono font-bold tabular-nums shrink-0", urgent ? "text-red-600" : "text-slate-500")}
                          title="Auto-returns to parker at 0:00"
                          data-testid={`pickup-countdown-${s.slot}`}
                        >
                          {formatParkCountdown(remaining)}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 truncate">
                        {s.parked_by_name ? `Parked by ${s.parked_by_name}` : "Parked"}
                      </p>
                    </div>
                    <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-[11px] font-semibold px-2.5 shrink-0">
                      <PhoneIncoming className="w-3 h-3 mr-1" /> Pick up
                    </Button>
                  </button>
                );
              })}
            </div>
            <div className="px-3 py-1.5 text-[10px] text-slate-400 bg-slate-50 text-center border-t border-slate-100">
              Auto-returns to parker after 3:00
            </div>
          </div>
        );
      })()}

      {/* Custom Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Transfer to Custom Number</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Input placeholder="+1 (555) 000-0000" value={transferNumber} onChange={e => setTransferNumber(e.target.value)} className="font-mono" data-testid="custom-transfer-input" />
            <div className="flex gap-2">
              <Button className="flex-1 bg-blue-500 hover:bg-violet-600" onClick={() => transferCall(transferNumber)} data-testid="custom-transfer-submit"><PhoneForwarded className="w-4 h-4 mr-2" /> Transfer</Button>
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ======= POST-CALL DOCUMENTATION MODAL =======
          Auto-pops after every call ≥3s. Auto-detects caller from
          phone via /api/communications/match-caller. Agent picks the
          match → drops into the full CommunicationLogModal pre-filled
          with caller info + source_call_sid. Dawn picked OPTIONAL
          (no enforcement) so the Skip button is a first-class option.
          As of iter-428 the modal also carries a **+ Save Contact**
          button that opens an inline mini-form and POSTs to
          /api/contacts so unknown callers can be added to the shared
          directory without leaving the call summary. */}
      {postCallContext && (
        <PostCallDocumentationModal
          callContext={postCallContext}
          onClose={() => setPostCallContext(null)}
        />
      )}

      {/* ======= LIVE-CALL SAVE-CONTACT MINI-MODAL =======
          Triggered by the pill under the caller phone in the overlay.
          Uses the same shared SaveContactForm as the post-call flow so
          duplicate detection (via /api/contacts/by-phone) fires the
          same amber banner whether we're mid-call or post-call. */}
      {saveContactPhone && (
        <div
          className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setSaveContactPhone(null)}
          data-testid="live-save-contact-backdrop"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="live-save-contact-modal"
          >
            <SaveContactForm
              phone={saveContactPhone}
              onCancel={() => setSaveContactPhone(null)}
              onSaved={(c) => {
                const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || "New contact";
                toast.success(`Saved ${name} to Contacts`);
                setSaveContactPhone(null);
              }}
            />
          </div>
        </div>
      )}
    </CallContext.Provider>
  );
}
